import _ from 'lodash';
import { sqDurationStore } from '@/core/core.stores';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { BaseToolStore } from '@/toolSelection/baseTool.store';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { OutputType } from '@/tools/selfOrganizingMaps/selfOrganizingMaps.constants';
import type { BaseToolPersistedState, ToolItem, ToolParameter } from '@/toolSelection/baseTool.store';
import type { Capsule } from '@/utilities/datetime.constants';

export const DEFAULT_DURATION = { value: 0, units: 'min' };

interface DurationItem {
  value: number;
  units: string;
}

interface SelfOrganizingMapsState extends BaseToolPersistedState {
  windowStart: number;
  windowEnd: number;
  window: any; // computed property using monkey()
  modelPropertiesExpanded: boolean;
  color: string;
  anomalyThreshold: number;
  sigma: number;
  learningRate: number;
  gridDimension: number | '';
  numIterations: number | '';
  randomOrder: boolean;
  outputType: OutputType;
  cleanCapsules: boolean;
  capsuleMinDuration: DurationItem;
  capsuleMergeDuration: DurationItem;
}

export type SelfOrganizingMapsDehydratedState = Omit<SelfOrganizingMapsState, 'window'>;

export class SelfOrganizingMapsStore extends BaseToolStore {
  static readonly storeName = 'sqSelfOrganizingMapsStore';
  type = TREND_TOOLS.SELF_ORGANIZING_MAPS;
  parameterDefinitions = {
    condition: {
      predicate: ['name', 'condition'],
    },
    restrictToCondition: {
      predicate: ['name', 'restrictToCondition'],
    },
    inputSignals: {
      predicate: (parameter: ToolParameter) => {
        const name = parameter.name ?? '';
        return name !== 'condition' && name !== 'restrictToCondition';
      },
      multiple: true,
    },
  };

  initialize(): void {
    const initialState: SelfOrganizingMapsState = {
      ...BASE_TOOL_COMMON_PROPS,
      windowStart: sqDurationStore.getState().displayRange.start,
      windowEnd: sqDurationStore.getState().displayRange.end,
      window: this.monkey(['windowStart'], ['windowEnd'], (startTime, endTime) => ({ startTime, endTime })),
      modelPropertiesExpanded: false,
      color: '',
      anomalyThreshold: 95,
      sigma: 1.0,
      learningRate: 0.5,
      gridDimension: '',
      numIterations: '',
      randomOrder: false,
      outputType: OutputType.CONDITION,
      cleanCapsules: false,
      capsuleMinDuration: DEFAULT_DURATION,
      capsuleMergeDuration: DEFAULT_DURATION,
    };
    this.state = this.immutable(initialState);
  }

  get inputSignals(): ToolItem[] {
    return this.state.get('inputSignals');
  }

  get condition(): ToolItem & { id: string } {
    return this.state.get('condition');
  }

  get restrictToCondition(): ToolItem & { id: string } {
    return this.state.get('restrictToCondition');
  }

  get window(): Capsule {
    return this.state.get('window');
  }

  get color(): string {
    return this.state.get('color');
  }

  get modelPropertiesExpanded(): boolean {
    return this.state.get('modelPropertiesExpanded');
  }

  get anomalyThreshold(): number {
    return this.state.get('anomalyThreshold');
  }

  get sigma(): number {
    return this.state.get('sigma');
  }

  get learningRate(): number {
    return this.state.get('learningRate');
  }

  get gridDimension(): number | '' {
    return this.state.get('gridDimension');
  }

  get numIterations(): number | '' {
    return this.state.get('numIterations');
  }

  get randomOrder(): boolean {
    return this.state.get('randomOrder');
  }

  get outputType(): OutputType {
    return this.state.get('outputType');
  }

  get cleanCapsules(): boolean {
    return this.state.get('cleanCapsules');
  }

  get capsuleMinDuration(): DurationItem {
    return this.state.get('capsuleMinDuration');
  }

  get capsuleMergeDuration(): DurationItem {
    return this.state.get('capsuleMergeDuration');
  }

  dehydrate(): SelfOrganizingMapsDehydratedState {
    return this.state.serialize();
  }

  rehydrate(dehydratedState: Partial<SelfOrganizingMapsDehydratedState>): void {
    this.state.merge(dehydratedState);
  }

  setTrainingWindow(payload: { window: Capsule }): void {
    this.state.set('windowStart', _.get(payload.window, 'startTime'));
    this.state.set('windowEnd', _.get(payload.window, 'endTime'));
  }

  setModelPropertiesExpanded(payload: { modelPropertiesExpanded: boolean }): void {
    this.state.set('modelPropertiesExpanded', payload.modelPropertiesExpanded);
  }

  setAnomalyThreshold(payload: { anomalyThreshold: number }): void {
    this.state.set('anomalyThreshold', payload.anomalyThreshold);
  }

  setSigma(payload: { sigma: number }): void {
    this.state.set('sigma', payload.sigma);
  }

  setLearningRate(payload: { learningRate: number }): void {
    this.state.set('learningRate', payload.learningRate);
  }

  setGridDimension(payload: { gridDimension: number | '' }): void {
    this.state.set('gridDimension', payload.gridDimension);
  }

  setNumIterations(payload: { numIterations: number | '' }): void {
    this.state.set('numIterations', payload.numIterations);
  }

  setRandomOrder(payload: { randomOrder: boolean }): void {
    this.state.set('randomOrder', payload.randomOrder);
  }

  setoutputType(payload: { outputType: OutputType }): void {
    this.state.set('outputType', payload.outputType);
  }

  setColor(payload: { color: string }): void {
    this.state.set('color', payload.color);
  }

  setCleanCapsules(payload: { cleanCapsules: boolean }): void {
    this.state.set('cleanCapsules', payload.cleanCapsules);
  }

  setCapsuleMinDuration(payload: { capsuleMinDuration: DurationItem }): void {
    this.state.set('capsuleMinDuration', payload.capsuleMinDuration);
  }

  setCapsuleMergeDuration(payload: { capsuleMergeDuration: DurationItem }): void {
    this.state.set('capsuleMergeDuration', payload.capsuleMergeDuration);
  }

  modifyConfigParams<TConfig extends Record<string, unknown>>(config: TConfig): TConfig {
    delete (config as Record<string, unknown>)['modelPropertiesExpanded'];
    return config;
  }

  protected readonly handlers = {
    ...this.baseHandlers,
    SOM_SET_TRAINING_WINDOW: this.setTrainingWindow,
    SOM_SET_MODEL_PROPERTIES_EXPANDED: this.setModelPropertiesExpanded,
    SOM_SET_ANOMALY_THRESHOLD: this.setAnomalyThreshold,
    SOM_SET_SIGMA: this.setSigma,
    SOM_SET_LEARNING_RATE: this.setLearningRate,
    SOM_SET_GRID_DIMENSION: this.setGridDimension,
    SOM_SET_NUM_ITERATIONS: this.setNumIterations,
    SOM_SET_RANDOM_ORDER: this.setRandomOrder,
    SOM_SET_OUTPUT_TYPE: this.setoutputType,
    SOM_SET_COLOR: this.setColor,
    SOM_SET_CLEAN_CAPSULES: this.setCleanCapsules,
    SOM_SET_CAPSULE_MIN_DURATION: this.setCapsuleMinDuration,
    SOM_SET_CAPSULE_MERGE_DURATION: this.setCapsuleMergeDuration,
  };
}
